import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CreateCommanWay, ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { Card, CardBody, Button, Input, Textarea } from "@heroui/react";
import UseToast from "utils/AfToast";
import Spinner from "utils/Spinner";
import Select from "react-select";

const NewLeadSuggestion: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  // Form States
  const [title, setTitle] = useState<string>("-");
  const [desc, setDesc] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [company, setCompany] = useState<string>("");
  const [website, setWebsite] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [gst, setGst] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [contactPersonName, setContactPersonName] = useState<string>("");
  const [contactPersonPhone, setContactPersonPhone] = useState<string>("");
  const [contactPersonEmail, setContactPersonEmail] = useState<string>("");
  const [products, setProducts] = useState<any>([]);
  const [source, setSource] = useState<any>(null);
  const [priority, setPriority] = useState<any>({ value: 1, label: "Medium" });
  const [addedBy, setAddedBy] = useState<any>(null);
  const [other, setOther] = useState<string>("");

  // User List
  const [userList, setUserList] = useState<any>([]);
  const [productList, setProductList] = useState<any>([]);

  const sourceOptions = [
    { value: "Website", label: "Website" },
    { value: "Referral", label: "Referral" },
    { value: "Cold Call", label: "Cold Call" },
    { value: "Email Campaign", label: "Email Campaign" },
    { value: "Social Media", label: "Social Media" },
    { value: "Trade Show", label: "Trade Show" },
    { value: "Walk-in", label: "Walk-in" },
    { value: "Advertisement", label: "Advertisement" },
    { value: "Other", label: "Other" },
  ];

  const priorityOptions = [
    { value: 0, label: "Low" },
    { value: 1, label: "Medium" },
    { value: 2, label: "High" },
    { value: 3, label: "Urgent" },
  ];

  // Fetch Users
  const { refetch: refetchUsers } = useQuery(
    ["UsersList"],
    () => ListCommanWay("/admin/user/list", { isActive: true }, { paginate: 1000 }),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          let opt = [];
          data.data.data.forEach((p) => {
            opt.push({
              value: p.id,
              label: p.name + " (" + p.username + ")",
            });
          });
          setUserList(opt);
        }
      },
    }
  );

  // Fetch Products
  const { refetch: refetchProducts } = useQuery(["ProductsList"], () => ListCommanWay("/admin/product/list", {}, { paginate: 1000 }), {
    keepPreviousData: true,
    onSuccess(data) {
      if (data.status == "SUCCESS") {
        let opt = [];
        data.data.data.forEach((p) => {
          opt.push({
            value: p.product_name,
            label: p.product_name,
          });
        });
        setProductList(opt);
      }
    },
  });

  // Fetch Lead Suggestion Details (Edit Mode)
  const { refetch: refetchSuggestion } = useQuery(
    ["LeadSuggestionDetail", id],
    () => ListCommanWay("/admin/lead/suggestion-list", { id: id }, {}),
    {
      enabled: isEditMode,
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS" && data.data.data.length > 0) {
          let lead = data.data.data[0];
          setTitle(lead.title || "-");
          setDesc(lead.desc || "");
          setName(lead.name || "");
          setEmail(lead.email || "");
          setPhone(lead.phone || "");
          setCompany(lead.company || "");
          setWebsite(lead.website || "");
          setAddress(lead.address || "");
          setGst(lead.gst || "");
          setCity(lead.city || "");
          setContactPersonName(lead.contactPersonName || "");
          setContactPersonPhone(lead.contactPersonPhone || "");
          setContactPersonEmail(lead.contactPersonEmail || "");

          // Set products
          if (lead.products && Array.isArray(lead.products)) {
            const selectedProducts = lead.products.map((p) => ({
              value: p.product_name || p,
              label: p.product_name || p,
            }));
            setProducts(selectedProducts);
          }

          setSource(lead.source ? { value: lead.source, label: lead.source } : null);
          setPriority(lead.priority ? { value: lead.priority, label: lead.priority } : null);
          setAddedBy(lead.addedBy ? { value: lead.addedBy, label: lead.addedBy } : null);
          setOther(lead.other || "");
        }
      },
    }
  );

  // Create Lead Suggestion
  const { mutate: mutateCreateSuggestion, isLoading: isCreating } = useMutation(
    (data: any) =>
      CreateCommanWay({
        url: "/admin/lead/suggest",
        data: data,
      }),
    {
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          UseToast("Lead Suggestion Created Successfully", "success");
          navigate("/af/lead-suggestions");
        } else {
          UseToast(data.message, "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  // Update Lead Suggestion
  const { mutate: mutateUpdateSuggestion, isLoading: isUpdating } = useMutation((data: any) => UpdateCommanWay(data), {
    onSuccess(data) {
      if (data.status == "SUCCESS") {
        UseToast("Lead Suggestion Updated Successfully", "success");
        refetchSuggestion();
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const handleSubmit = () => {
    if (!company || !contactPersonName || !contactPersonPhone || !contactPersonEmail) {
      UseToast("Please fill all required fields (Company, Contact Name, Contact Phone, Contact Email)", "error");
      return;
    }

    const suggestionData = {
      title,
      desc,
      name,
      email,
      phone,
      company,
      website,
      address,
      gst,
      city,
      contactPersonName,
      contactPersonPhone,
      contactPersonEmail,
      products: products.map((p) => p.value).join(","),
      source: source?.value || null,
      priority: priority?.value || 1,
      addedBy: addedBy?.value || undefined,
      other,
    };

    if (isEditMode) {
      mutateUpdateSuggestion({
        url: "/admin/lead/suggest/" + id,
        data: suggestionData,
      });
    } else {
      mutateCreateSuggestion(suggestionData);
    }
  };

  return (
    <>
      {(isCreating || isUpdating) && <Spinner />}

      <div className="px-4 py-2 [&_input]:border-none [&_input]:px-0 [&_textarea]:border-none [&_textarea]:px-0 ">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold font-inter">{isEditMode ? "View Lead Suggestion" : "New Lead Suggestion"}</h1>
          <div className="flex gap-2">
            <Button color="default" variant="flat" onPress={() => navigate("/af/lead-suggestions")}>
              Cancel
            </Button>
            {!isEditMode && (
              <Button color="primary" isLoading={isCreating || isUpdating} onPress={handleSubmit}>
                Submit Suggestion
              </Button>
            )}
          </div>
        </div>

        {/* Quick Info Section */}
        <div className="grid grid-cols-3 gap-4 mb-4 rounded-lg">
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-600 mb-1">Priority</label>
            <Select
              options={priorityOptions}
              value={priority}
              onChange={setPriority}
              placeholder="Select Priority"
              className="text-sm z-20"
              isClearable
              isDisabled={isEditMode}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-600 mb-1">Source</label>
            <Select
              options={sourceOptions}
              value={source}
              onChange={setSource}
              placeholder="Select Source"
              className="text-sm z-20"
              isClearable
              isDisabled={isEditMode}
            />
          </div>
          {/*<div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-600 mb-1">Suggest To (Sales Person)</label>
            <Select
              options={userList}
              value={addedBy}
              onChange={setAddedBy}
              placeholder="Select User"
              className="text-sm z-20"
              isClearable
              isDisabled={isEditMode}
            />
          </div>*/}
        </div>

        {/* Main Form */}
        <Card>
          <CardBody className="p-4">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <div className="space-y-4">
                {/* Basic Information */}
                <div className="border-b pb-3">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Basic Information</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {/*<Input
                      label="Title"
                      labelPlacement="outside-top"
                      placeholder="Enter lead title"
                      variant="bordered"
                      value={title}
                      onValueChange={setTitle}
                      size="sm"
                      isRequired
                      isDisabled={isEditMode}
                    />*/}
                    <Textarea
                      label="Description"
                      labelPlacement="outside-top"
                      placeholder="Enter lead description"
                      variant="bordered"
                      value={desc}
                      onValueChange={setDesc}
                      size="sm"
                      minRows={3}
                      isDisabled={isEditMode}
                    />
                  </div>
                </div>

                {/* Customer Information */}
                <div className="border-b pb-3">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Customer Information</h3>
                  <div className="grid grid-cols-4 gap-4">
                    <Input
                      label="Customer Name"
                      labelPlacement="outside-top"
                      placeholder="Enter customer name"
                      variant="bordered"
                      value={name}
                      onValueChange={setName}
                      size="sm"
                      isRequired
                      isDisabled={isEditMode}
                    />
                    <Input
                      label="Customer Email"
                      labelPlacement="outside-top"
                      placeholder="Enter customer email"
                      variant="bordered"
                      type="email"
                      value={email}
                      onValueChange={setEmail}
                      size="sm"
                      isRequired
                      isDisabled={isEditMode}
                    />
                    <Input
                      label="Customer Phone"
                      labelPlacement="outside-top"
                      placeholder="Enter customer phone number"
                      variant="bordered"
                      value={phone}
                      onValueChange={setPhone}
                      size="sm"
                      isRequired
                      isDisabled={isEditMode}
                    />
                    <Input
                      label="Company"
                      labelPlacement="outside-top"
                      placeholder="Enter company name"
                      variant="bordered"
                      value={company}
                      onValueChange={setCompany}
                      size="sm"
                      isDisabled={isEditMode}
                    />
                  </div>
                </div>

                {/* Contact Person Information */}
                <div className="border-b pb-3">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Contact Person Information</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <Input
                      label="Contact Person Name"
                      labelPlacement="outside-top"
                      placeholder="Enter contact person name"
                      variant="bordered"
                      value={contactPersonName}
                      onValueChange={setContactPersonName}
                      size="sm"
                      isDisabled={isEditMode}
                    />
                    <Input
                      label="Contact Person Phone"
                      labelPlacement="outside-top"
                      placeholder="Enter contact phone"
                      variant="bordered"
                      value={contactPersonPhone}
                      onValueChange={setContactPersonPhone}
                      size="sm"
                      isDisabled={isEditMode}
                    />
                    <Input
                      label="Contact Person Email"
                      labelPlacement="outside-top"
                      placeholder="Enter contact email"
                      variant="bordered"
                      type="email"
                      value={contactPersonEmail}
                      onValueChange={setContactPersonEmail}
                      size="sm"
                      isDisabled={isEditMode}
                    />
                  </div>
                </div>

                {/* Additional Information */}
                <div className="border-b pb-3">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Additional Information</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <Input
                      label="Website"
                      labelPlacement="outside-top"
                      placeholder="Enter website URL"
                      variant="bordered"
                      value={website}
                      onValueChange={setWebsite}
                      size="sm"
                      isDisabled={isEditMode}
                    />
                    <Input
                      label="GST Number"
                      labelPlacement="outside-top"
                      placeholder="Enter GST number"
                      variant="bordered"
                      value={gst}
                      onValueChange={setGst}
                      size="sm"
                      isDisabled={isEditMode}
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Products</label>
                      <Select
                        options={productList}
                        value={products}
                        onChange={setProducts}
                        placeholder="Select Products"
                        className="text-sm z-20"
                        isMulti
                        isClearable
                        closeMenuOnSelect={false}
                        isDisabled={isEditMode}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 mt-4">
                    <Textarea
                      label="Address"
                      labelPlacement="outside-top"
                      placeholder="Enter full address"
                      variant="bordered"
                      value={address}
                      onValueChange={setAddress}
                      size="sm"
                      minRows={2}
                      isDisabled={isEditMode}
                    />
                    <Input
                      label="City"
                      labelPlacement="outside-top"
                      placeholder="Enter city"
                      variant="bordered"
                      value={city}
                      onValueChange={setCity}
                      size="sm"
                      isDisabled={isEditMode}
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Notes</h3>
                  <Textarea
                    label="Additional Notes"
                    labelPlacement="outside-top"
                    placeholder="Any additional information about the lead"
                    variant="bordered"
                    value={other}
                    onValueChange={setOther}
                    size="sm"
                    minRows={3}
                    isDisabled={isEditMode}
                  />
                </div>
              </div>
            </form>
          </CardBody>
        </Card>

        {isEditMode && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Note:</span> This is a view-only mode. Lead suggestions cannot be edited once submitted. The
              sales team will review and convert this to an actual lead if appropriate.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default NewLeadSuggestion;
